import { useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { LoadingIndicator, RenderWhen } from "../../../../components";
import SelectRowsProvider from "../../../../enhancers/SelectRowsProvider";
import Typography from "../../../../components/typography/Typography";
import useAttributionData from "../../hooks/useAttributionData";
import ActorCampaigns from "./cards/ActorCampaigns";
import ActorInsights from "./cards/ActorInsights";
import ActorIOC from "./cards/ActorIOC";
import ActorSources from "./cards/ActorSources";
import ActorTTPS from "./cards/ActorTTPS";
import ActorVulnerabilities from "./cards/ActorVulnerabilities";
import RelatedActors from "./cards/RelatedActors";
import SingleActorSampleAttribution from "./cards/SingleActorSampleAttribution";

interface AttributionTabContentProps {
  ioc: string;
  threatName?: string;
}

const AttributionTabContent = ({
  ioc,
  threatName
}: AttributionTabContentProps) => {
  const { iocData, actors, isIocLoading, getActorName, getActorDescription } =
    useAttributionData({ ioc, threatName });
  const [activeActorIndex, setActiveActorIndex] = useState(0);

  if (isIocLoading) return <LoadingIndicator />;

  const hasActors = actors.length > 0;

  return (
    <RenderWhen
      condition={hasActors}
      fallback={
        <Typography variant="subtitle4">
          No actor data available for this sample.
        </Typography>
      }
    >
      {actors.length === 1 ? (
        <div className="flex flex-column flex-gap-3">
          <SingleActorSampleAttribution sampleIOCData={iocData} />
          <ActorInsights
            actorName={getActorName(0)}
            description={getActorDescription(0)}
          />
          <SelectRowsProvider>
            <ActorIOC actorName={getActorName(0)} />
          </SelectRowsProvider>
          <ActorCampaigns actorName={getActorName(0)} />
          <ActorTTPS actorName={getActorName(0)} />
          <ActorVulnerabilities actorName={getActorName(0)} />
          <RelatedActors actorName={getActorName(0)} />
          <ActorSources actorName={getActorName(0)} />
        </div>
      ) : (
        <Tab.Container
          id="actor-tabs"
          defaultActiveKey="0"
          activeKey={activeActorIndex.toString()}
          onSelect={key => setActiveActorIndex(Number(key))}
        >
          <Nav className="mb-20 border-bottom">
            {actors.map((actorName, index) => (
              <Nav.Item key={actorName}>
                <Nav.Link
                  eventKey={index.toString()}
                  style={{
                    textDecoration: "underline",
                    fontWeight: activeActorIndex === index ? "bold" : "normal",
                    cursor: "pointer"
                  }}
                >
                  {actorName.charAt(0).toUpperCase() + actorName.slice(1)}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <Tab.Content>
            {actors.map((_, index) => (
              <Tab.Pane key={index} eventKey={index.toString()}>
                <div className="flex flex-column flex-gap-3">
                  <SingleActorSampleAttribution sampleIOCData={iocData} />
                  <ActorInsights
                    actorName={getActorName(index)}
                    description={getActorDescription(index)}
                  />
                  <SelectRowsProvider>
                    <ActorIOC actorName={getActorName(index)} />
                  </SelectRowsProvider>
                  <ActorCampaigns actorName={getActorName(index)} />
                  <ActorTTPS actorName={getActorName(index)} />
                  <ActorVulnerabilities actorName={getActorName(index)} />
                  <RelatedActors actorName={getActorName(index)} />
                  <ActorSources actorName={getActorName(index)} />
                </div>
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>
      )}
    </RenderWhen>
  );
};

export default AttributionTabContent;
